import { useEffect, useRef, useState } from 'react'

import { fetchJson } from 'api/fetcher'
import { Disclosure } from 'api/lender/getLenderDisclosures'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import useSwr from 'swr'

export interface UseLenderDisclosures {
    disclosures: Disclosure[] | undefined
    loading: boolean
    error: Error | null
    retryAttemptsExhausted: boolean
}

const DISCLOSURE_RETRY_COUNT = 3
const DISCLOSURE_RETRY_BASE_INTERVAL_MS = 1000
const LENDER_ID_WARN_DELAY_MS = 5000

export function useLenderDisclosures(
    lenderId: number | undefined,
): UseLenderDisclosures {
    const [retryAttemptsExhausted, setRetryAttemptsExhausted] =
        useState<boolean>(false)

    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const swrKey = lenderId ? `v3/lenders/${lenderId}/disclosures/` : null

    const {
        data: disclosures,
        error,
        isLoading,
    } = useSwr(swrKey, fetchJson<Disclosure[]>, {
        errorRetryCount: DISCLOSURE_RETRY_COUNT,
        onErrorRetry: (_error, _key, _config, revalidate, context) => {
            const { retryCount } = context

            if (retryCount >= DISCLOSURE_RETRY_COUNT) {
                setRetryAttemptsExhausted(true)

                OctaneLogger.error(
                    `Lender disclosures failed to load after ${retryCount} retry attempts — application submission has been disabled`,
                    { lenderId },
                    _error,
                )

                return
            }

            const timeoutMs =
                DISCLOSURE_RETRY_BASE_INTERVAL_MS * 2 ** retryCount

            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current)
            }

            retryTimerRef.current = setTimeout(
                () => revalidate({ retryCount }),
                timeoutMs,
            )
        },
    })

    useEffect(() => {
        setRetryAttemptsExhausted(false)

        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current)
            }
        }
    }, [swrKey])

    useEffect(() => {
        if (disclosures) {
            setRetryAttemptsExhausted(false)
        }
    }, [disclosures])

    useEffect(() => {
        if (lenderId === undefined) {
            warnTimerRef.current = setTimeout(() => {
                OctaneLogger.warn(
                    `useLenderDisclosures: lenderId has remained undefined for ${LENDER_ID_WARN_DELAY_MS}ms — disclosures fetch has not started`,
                )
            }, LENDER_ID_WARN_DELAY_MS)
        } else {
            OctaneLogger.debug(
                'useLenderDisclosures: lenderId resolved, triggering disclosures fetch',
                { lenderId },
            )
        }

        return () => {
            if (warnTimerRef.current) {
                clearTimeout(warnTimerRef.current)
                warnTimerRef.current = null
            }
        }
    }, [lenderId])

    return {
        disclosures,
        loading: isLoading,
        error,
        retryAttemptsExhausted,
    }
}

export default useLenderDisclosures
